import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  X, ChevronRight, ChevronLeft, HelpCircle, Sparkles,
  LayoutDashboard, FileWarning, AlertTriangle, ClipboardCheck,
  Scale, BookOpen, Plane, Brain, Settings, Play, CheckCircle2,
  Radar, Bird, ShieldCheck,
} from 'lucide-react';

/* --------------------------------------------------------------------------
 * Role-aware guided product tours.
 *
 * Each role gets a short (5–8 step) tour of THEIR most-used screens instead
 * of a generic 18-step marathon that most users abandoned. The steps share
 * a common intro/outro and are stitched together at render time.
 *
 * Completion is tracked per-role in localStorage so if a user changes role
 * they'll get the correct tour on next login. Users can restart from the
 * Help Center.
 * ------------------------------------------------------------------------ */

const INTRO_STEP = {
  id: 'welcome',
  title: 'Welcome to AeroGuard SMS',
  content:
    'A quick 60-second tour of the screens you\'ll use most. You can skip or restart it from the Help Center at any time.',
  target: null,
  position: 'center',
  icon: Sparkles,
};

const OUTRO_STEP = {
  id: 'complete',
  title: 'You\'re all set!',
  content:
    'That\'s the essentials. Need more help? Click the sparkle bubble bottom-right to ask the AI, or open the Help Center from the sidebar.',
  target: null,
  position: 'center',
  icon: CheckCircle2,
};

const STEP = {
  dashboard: {
    id: 'dashboard',
    title: 'Your Safety Dashboard',
    content: 'Open incidents, risk score, audit status, and pending actions — all at a glance.',
    target: '[data-testid="nav-dashboard"]',
    position: 'right', icon: LayoutDashboard,
  },
  incidents: {
    id: 'incidents',
    title: 'File a Report',
    content: 'File incidents, hazards, and occurrences here. Anonymous option available. Works offline — reports sync when you\'re back online.',
    target: '[data-testid="nav-incidents"]',
    position: 'right', icon: FileWarning,
  },
  wildlife: {
    id: 'wildlife',
    title: 'Wildlife Strikes',
    content: 'Log bird / wildlife strikes and view the Hotspot Map to see where they cluster — GACA-aligned Annex 14 §9.4.',
    target: '[data-testid="nav-wildlife-strikes"]',
    position: 'right', icon: Bird,
  },
  riskRegister: {
    id: 'risk-register',
    title: 'Risk Register',
    content: 'Every identified hazard with its severity × likelihood score. Owners and controls live here.',
    target: '[data-testid="nav-risk-register"]',
    position: 'right', icon: AlertTriangle,
  },
  riskRadar: {
    id: 'risk-radar',
    title: 'Live Risk Radar',
    content: 'Real-time operational risk — weather, NOTAMs, crew fatigue, and maintenance status in one view.',
    target: '[data-testid="nav-risk-radar"]',
    position: 'right', icon: Radar,
  },
  audits: {
    id: 'audits',
    title: 'Audits',
    content: 'Plan, execute, and close audits. Each finding creates a linked CAPA (task to fix a safety problem).',
    target: '[data-testid="nav-audits"]',
    position: 'right', icon: ClipboardCheck,
  },
  compliance: {
    id: 'compliance',
    title: 'Compliance Center',
    content: 'GAP analysis against GACA / ICAO / EASA / FAA plus the regulatory submission pipeline.',
    target: '[data-testid="nav-compliance-center"]',
    position: 'right', icon: Scale,
  },
  fdm: {
    id: 'fdm',
    title: 'FDM / FOQA',
    content: 'Flight data monitoring — detect exceedances, analyse trends, and get AI-flagged anomalies.',
    target: '[data-testid="nav-fdm"]',
    position: 'right', icon: ShieldCheck,
  },
  crew: {
    id: 'crew',
    title: 'Crew Management',
    content: 'Qualifications, FTL compliance, and 48-hour AI fatigue forecast for every crew member.',
    target: '[data-testid="nav-crew-management"]',
    position: 'right', icon: Plane,
  },
  ai: {
    id: 'ai',
    title: 'AI Risk Prediction',
    content: 'GPT-5.2 spots patterns, suggests root causes, and drafts CAPAs. Always review before actioning.',
    target: '[data-testid="nav-ai-assistant"]',
    position: 'right', icon: Brain,
  },
  training: {
    id: 'training',
    title: 'Training & Currency',
    content: 'Track training records, certifications, and upcoming expiries. Alerts fire before things lapse.',
    target: '[data-testid="nav-training-promotion"]',
    position: 'right', icon: BookOpen,
  },
  settings: {
    id: 'settings',
    title: 'Administration',
    content: 'Users, roles, organisations, LDAP sync, regulatory API configs, and system settings.',
    target: '[data-testid="nav-settings"]',
    position: 'right', icon: Settings,
  },
  help: {
    id: 'help',
    title: 'Help Center',
    content: 'Full user guide, role-specific playbooks, and the AI Help Assistant — all in one place.',
    target: '[data-testid="nav-help-center"]',
    position: 'right', icon: HelpCircle,
  },
};

// The essential short tour per role — 4–6 middle steps each. Wrapped with
// intro/outro at render time.
const ROLE_TOURS = {
  admin: [STEP.dashboard, STEP.settings, STEP.compliance, STEP.audits, STEP.ai, STEP.help],
  safety_manager: [STEP.dashboard, STEP.incidents, STEP.riskRegister, STEP.audits, STEP.compliance, STEP.ai, STEP.help],
  safety_officer: [STEP.dashboard, STEP.incidents, STEP.wildlife, STEP.riskRegister, STEP.help],
  auditor: [STEP.dashboard, STEP.audits, STEP.compliance, STEP.help],
  investigator: [STEP.dashboard, STEP.incidents, STEP.ai, STEP.help],
  quality_manager: [STEP.dashboard, STEP.audits, STEP.compliance, STEP.help],
  executive: [STEP.dashboard, STEP.compliance, STEP.ai, STEP.help],
  inspector: [STEP.dashboard, STEP.audits, STEP.compliance, STEP.help],
  pilot: [STEP.dashboard, STEP.incidents, STEP.wildlife, STEP.crew, STEP.help],
  default: [STEP.dashboard, STEP.incidents, STEP.riskRegister, STEP.audits, STEP.compliance, STEP.help],
};

const buildTourForRole = (role) => {
  const middle = ROLE_TOURS[role] || ROLE_TOURS.default;
  return [INTRO_STEP, ...middle, OUTRO_STEP];
};

const completionKey = (role) => `aeroguard_tour_completed_${role || 'default'}`;

export default function GuidedTour({ onComplete }) {
  const { user } = useAuth();
  const role = user?.role || 'default';
  // MEMOIZE steps by role so the reference is stable across renders. Without
  // this, `steps` is a fresh array every render → useEffect below fires every
  // render → setTooltipPosition → re-render → INFINITE LOOP. See iter-91 fix.
  const steps = useMemo(() => buildTourForRole(role), [role]);

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Auto-start on first login per role, or when explicitly requested.
  // Deferred while the first-run OnboardingWizard is on-screen (admins) so
  // the two full-screen intros don't stack on top of each other.
  useEffect(() => {
    const tourCompleted = localStorage.getItem(completionKey(role));
    const tourActive = localStorage.getItem('aeroguard_tour_active');
    const onboardingActive = document.querySelector('[data-testid="onboarding-wizard"]');

    if (tourActive === 'true') {
      // User explicitly asked to (re)start — clear the completion flag so
      // future logins auto-start again for a new role assignment.
      setIsActive(true);
      localStorage.removeItem('aeroguard_tour_active');
    } else if (!tourCompleted && !onboardingActive) {
      // First-time-for-this-role auto-start — but only if onboarding is done.
      setTimeout(() => {
        if (!document.querySelector('[data-testid="onboarding-wizard"]')) {
          setIsActive(true);
        }
      }, 1200);
    }
  }, [role]);

  // Position tooltip based on target element
  useEffect(() => {
    if (!isActive) return;

    const step = steps[currentStep];
    if (!step) return;

    if (step.position === 'center' || !step.target) {
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const targetEl = document.querySelector(step.target);
    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      targetEl.classList.add('tour-highlight');
      let top;
      let left;
      switch (step.position) {
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 20;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 20;
          break;
        case 'bottom':
          top = rect.bottom + 20;
          left = rect.left + rect.width / 2;
          break;
        case 'top':
          top = rect.top - 20;
          left = rect.left + rect.width / 2;
          break;
        default:
          top = '50%';
          left = '50%';
      }
      setTooltipPosition({ top, left });

      return () => {
        targetEl.classList.remove('tour-highlight');
      };
    }
  }, [isActive, currentStep, steps]);

  const closeTour = useCallback(() => {
    document.querySelectorAll('.tour-highlight').forEach((el) => {
      el.classList.remove('tour-highlight');
    });
    setIsActive(false);
    localStorage.setItem(completionKey(role), 'true');
    if (onComplete) onComplete();
  }, [onComplete, role]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const currentTarget = steps[currentStep].target;
      if (currentTarget) {
        document.querySelector(currentTarget)?.classList.remove('tour-highlight');
      }
      setCurrentStep((prev) => prev + 1);
    } else {
      closeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const currentTarget = steps[currentStep].target;
      if (currentTarget) {
        document.querySelector(currentTarget)?.classList.remove('tour-highlight');
      }
      setCurrentStep((prev) => prev - 1);
    }
  };

  const skipTour = () => closeTour();

  if (!isActive) return null;

  const step = steps[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;
  const roleLabel = (user?.role || 'user').replace(/_/g, ' ');

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[9998]"
        onClick={(e) => e.stopPropagation()}
      />

      <div
        className="fixed z-[9999] w-[400px] animate-in fade-in-0 zoom-in-95"
        style={{
          top: typeof tooltipPosition.top === 'number' ? `${tooltipPosition.top}px` : tooltipPosition.top,
          left: typeof tooltipPosition.left === 'number' ? `${tooltipPosition.left}px` : tooltipPosition.left,
          transform: tooltipPosition.transform || (step.position === 'right' ? 'translateY(-50%)' : 'none'),
        }}
        data-testid="tour-tooltip"
      >
        <Card className="bg-slate-900 border-purple-500/50 shadow-xl shadow-purple-500/20">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                      Step {currentStep + 1} of {steps.length}
                    </Badge>
                    <Badge className="bg-sky-500/15 text-sky-300 text-[10px] capitalize">
                      {roleLabel}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-white">{step.title}</h3>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={skipTour}
                className="text-slate-400 hover:text-white"
                data-testid="tour-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="px-4 pt-3">
              <Progress value={progress} className="h-1" />
            </div>

            <div className="p-4">
              <p className="text-slate-300 leading-relaxed">{step.content}</p>
            </div>

            <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-800/50">
              <Button
                variant="ghost"
                onClick={skipTour}
                className="text-slate-400"
                data-testid="tour-skip-btn"
              >
                Skip Tour
              </Button>
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    className="border-slate-600"
                    data-testid="tour-back-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                <Button
                  onClick={nextStep}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="tour-next-btn"
                >
                  {currentStep === steps.length - 1 ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Finish
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {step.position === 'right' && (
          <div
            className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '10px solid transparent',
              borderBottom: '10px solid transparent',
              borderRight: '10px solid rgb(168 85 247 / 0.5)',
            }}
          />
        )}
      </div>

      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 9999 !important;
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3);
          border-radius: 8px;
          animation: tour-pulse 2s infinite;
        }
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3); }
          50%      { box-shadow: 0 0 0 8px rgba(168, 85, 247, 0.3), 0 0 30px rgba(168, 85, 247, 0.4); }
        }
      `}</style>
    </>
  );
}

// Export a button component to (re)start the tour for the current role
export function StartTourButton({ className }) {
  const startTour = () => {
    localStorage.setItem('aeroguard_tour_active', 'true');
    // Best-effort: clear all per-role completion flags so it always runs
    Object.keys(localStorage)
      .filter((k) => k.startsWith('aeroguard_tour_completed_'))
      .forEach((k) => localStorage.removeItem(k));
    // Legacy key from the old (non-role-aware) tour
    localStorage.removeItem('aeroguard_tour_completed');
    window.location.reload();
  };

  return (
    <Button onClick={startTour} className={className} data-testid="start-tour-btn">
      <Play className="w-4 h-4 mr-2" />
      Start Guided Tour
    </Button>
  );
}
